import React, { useContext, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { I18nContext } from '../context/translations/TranslationsContext.context';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { fmtEur, fmtNum } from '../utils/formatArcaNumbers';
import { RESCUE_REASONS } from '../utils/rescueDocsCatalog';
import createWithdrawalRequest from '../api/pension-plan-withdrawals/createWithdrawalRequest';
import {
	WithdrawalKind,
	WithdrawalReason
} from '../api/pension-plan-withdrawals/interfaces/WithdrawalRequest';
import RescueRequestSubmittedV2 from './RescueRequestSubmittedV2';
import { translateWithdrawalError } from '../utils/translateWithdrawalError';
import RescueSupportNoteV2 from './RescueSupportNoteV2';

/**
 * ⚠️ UX SCAFFOLD (tarea Notion "Definimos MVP rescates") — valida el flujo de
 * solicitud de rescate SOLO a nivel visual. No llama a ningún endpoint: hoy no
 * existe ninguna operación de escritura de rescate ni en Caser (SOAP) ni en
 * comparsa. NO SEGUIR ESTE ARCHIVO COMO REFERENCIA DE CALIDAD — sin tests,
 * sin conexión real a datos, solo para validar UX en la vista empleado.
 *
 * Este diálogo se abre para alguien que YA tiene un motivo de rescate
 * establecido (o, en el caso de María, jubilación histórica) — por eso el
 * flujo es directo: elegir cantidad (sin fecha: al ser "inmediato", se lanza
 * en cuanto se envía la solicitud — solo se avisa con letra pequeña de que
 * puede tardar hasta 7 días en llegar). El motivo se muestra igualmente (para
 * que quede claro bajo qué contingencia se rescata), pero solo es editable si
 * el motivo actual no es Jubilación — una vez jubilado, el motivo no cambia.
 *
 * Sin IBAN por ahora (quitado a petición de usuario, jul-2026): se pedirá
 * más adelante, no aquí — mismo criterio que SolicitarRescatePorMotivoDialogV2.
 *
 * Sin paso de contraseña ni de verificación de identidad (KYC) por ahora
 * (quitados a petición de usuario, jul-2026 — quedará para una tarea de
 * seguridad futura): tanto pedir el rescate por primera vez como cambiar de
 * motivo envían la solicitud directamente y pasan a la misma confirmación,
 * que ya avisa de que el equipo contactará para pedir la documentación
 * necesaria — no hace falta un paso adicional para eso.
 */

interface RescueRequestDialogV2Props {
	isOpen: boolean;
	onClose: () => void;
	availableAmount: number;
	// Email del usuario que confirma.
	email: string;
	// Motivo ya establecido, si lo hay (ver rescueDocsCatalog.ts). Si es
	// 'retirement' (Jubilación), el campo de motivo queda fijo y no editable.
	currentReason?: string | null;
	// Importe rescatable SOLO por antigüedad (tramo +10 años) — mismo criterio
	// que en ConfigurarRentaMensualDialogV2: si se pasa, el máximo se recalcula
	// a este valor en cuanto el motivo elegido sea "Antigüedad".
	seniorityAmount?: number;
	// Plan del que se rescata (`user_epp_config.id`) y token: sin ellos no se
	// puede registrar la solicitud en backend.
	userEppConfigId?: string | null;
	token?: string | null;
	// Aviso al padre de que ya hay una solicitud viva para este plan.
	onSubmitted?: (reason: string) => void;
}

const MIN_AMOUNT = 100;

const RescueRequestDialogV2: React.FC<RescueRequestDialogV2Props> = ({
	isOpen,
	onClose,
	availableAmount,
	email,
	currentReason,
	seniorityAmount,
	userEppConfigId,
	token,
	onSubmitted
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const [reason, setReason] = useState(currentReason || 'retirement');
	// Por normativa, "Antigüedad" solo da acceso al tramo +10 años, no al resto
	// del saldo — recalcula el máximo cada vez que cambia el motivo elegido.
	const effectiveAvailable =
		reason === 'seniority' && seniorityAmount != null
			? Math.min(availableAmount, seniorityAmount)
			: availableAmount;

	const [amount, setAmount] = useState(effectiveAvailable);
	const [step, setStep] = useState<'form' | 'submitted'>('form');
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);

	// Bloqueado solo si YA hay un rescate hecho por jubilación (currentReason
	// viene del padre, no del valor que se esté seleccionando ahora mismo) —
	// no basta con que el desplegable arranque en "Jubilación" por defecto.
	const isReasonLocked = currentReason === 'retirement';

	useEffect(() => {
		if (isOpen) {
			setReason(currentReason || 'retirement');
			setStep('form');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, currentReason]);

	// Si al abrir o cambiar de motivo el máximo baja (p. ej. a "Antigüedad"),
	// el importe ya elegido no puede quedar por encima del nuevo tope.
	useEffect(() => {
		setAmount((current) => (isOpen ? effectiveAvailable : current));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, effectiveAvailable]);

	const handleClose = () => {
		onClose();
	};

	// Se guarda la solicitud como movimiento "pendiente" (ver
	// rescueDocsCatalog.ts) y se muestra la confirmación con el plazo de
	// tramitación, en vez de cerrar el modal directamente.
	// Sólo avanza si el backend acepta: puede rechazarlo porque ya haya una
	// solicitud viva en este plan, y darlo por bueno dejaría a la persona
	// creyendo que la ha enviado.
	const handleSubmit = async () => {
		if (!token || !userEppConfigId) {
			setSubmitError(translate('rescue.solicitarDialog.errorNoPlan'));
			return;
		}

		setSubmitting(true);
		setSubmitError(null);

		try {
			await createWithdrawalRequest(token, {
				userEppConfigId,
				reason: reason as WithdrawalReason,
				kind: WithdrawalKind.PUNCTUAL,
				// Antigüedad va sin importe: no sabemos cuánto es rescatable.
				...(reason === 'seniority' ? {} : { amount })
			});

			onSubmitted?.(reason);
			setStep('submitted');
		} catch (caught) {
			setSubmitError(translateWithdrawalError(caught, translate));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
			<DialogContent className="max-w-lg">
				<div className="individual-v2 -m-6 p-6 rounded-lg">
					<DialogHeader>
						<DialogTitle className="font-heading">
							{translate('rescue.requestDialog.title')}
						</DialogTitle>
					</DialogHeader>

					{step === 'submitted' ? (
						<RescueRequestSubmittedV2 onClose={handleClose} />
					) : (
						<div className="space-y-4 mt-2">
							<p className="text-sm text-muted-foreground">
								{translate('rescue.requestDialog.availablePrefix')}{' '}
								<span className="font-semibold text-foreground">{fmtEur(effectiveAvailable)}</span>
								{reason === 'seniority' && seniorityAmount != null && (
									<span className="block text-xs mt-0.5">
										{translate('rescue.requestDialog.seniorityNote')}
									</span>
								)}
							</p>

							<div className="space-y-1.5">
								<Label htmlFor="rescue-reason">
									{translate('rescue.requestDialog.reasonLabel')}
								</Label>
								<Select value={reason} onValueChange={setReason} disabled={isReasonLocked}>
									<SelectTrigger id="rescue-reason">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{RESCUE_REASONS.map((r) => (
											<SelectItem key={r.value} value={r.value}>
												{translate(r.labelKey)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								{isReasonLocked && (
									<p className="text-xs text-muted-foreground">
										{translate('rescue.requestDialog.reasonLockedNote')}
									</p>
								)}
							</div>

							<div className="space-y-1.5">
								<div className="flex items-baseline justify-between">
									<Label>{translate('rescue.requestDialog.amountLabel')}</Label>
									<span className="text-2xl font-bold font-heading text-primary tracking-tight">
										{fmtEur(amount)}
									</span>
								</div>
								<Slider
									className="slider-arca"
									value={[amount]}
									onValueChange={([v]) => setAmount(v)}
									min={MIN_AMOUNT}
									max={effectiveAvailable}
									step={50}
								/>
								<div className="flex justify-between text-xs text-muted-foreground">
									<span>
										{translate('rescue.requestDialog.minPrefix')} {fmtNum(MIN_AMOUNT, 0)}€
									</span>
									<span>
										{translate('rescue.requestDialog.allPrefix')} {fmtEur(effectiveAvailable)}
									</span>
								</div>
							</div>

							{submitError && (
								<p role="alert" className="text-[13px] text-destructive text-center">
									{submitError}
								</p>
							)}
							<Button className="w-full" onClick={handleSubmit} disabled={submitting}>
								{submitting
									? translate('rescue.solicitarDialog.submitting')
									: translate('rescue.requestDialog.submitButton')}
							</Button>
							<p className="text-[11px] text-muted-foreground text-center">
								{translate('rescue.requestDialog.processingNote')}
							</p>
							<RescueSupportNoteV2 />
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default RescueRequestDialogV2;
